import type { AdBumperClip, AdSlot as PublicAdSlot } from '@foundation-player/types';

export { AdClipType, AdSlotType } from '@foundation-player/runtime';
export type { AdClip } from '@foundation-player/types';

/**
 * The AdSlot class represents a single ad slot and acts as a data container.
 *
 * @remarks
 * An ad slot is a single ad break in the content. It can contain multiple clips and be of different types.
 */
export type AdSlot = PublicAdSlot & {
  /**
   * Get the unique id of this slot
   *
   * @remarks
   * This id is only used for tracking purposes, especially if the ad slot is broken.
   *
   * @internal
   *
   * TODO: Visibility was changed from `public` to `internal`!
   */
  getId(): number;

  /**
   * Get the clip which is played before the commercials
   *
   * @internal
   */
  getOpener(): AdBumperClip | undefined;

  /**
   * Get the clip to play before a sponsoring ad clip
   *
   * @internal
   */
  getBumper(): AdBumperClip | undefined;

  /**
   * Get the clip which is played after the commercials, but before a sponsoring clip
   *
   * @internal
   */
  getCloser(): AdBumperClip | undefined;

  /**
   * Get the ad call for this slot.
   * NOTE: the ad call may include placeholders which must be replaced before sending the ad call.
   *
   * @internal
   */
  getAdCall(): string;

  /**
   * Prepare this ad slot for playback
   *
   * @internal
   */
  initialise(): void;

  /**
   * Check if this ad slot is being prepared for playback
   *
   * @internal
   *
   * TODO: Visibility was changed from `public` to `internal`!
   */
  isInitialised(): boolean;

  /**
   * Set ad slot as loaded and ready to be played
   *
   * @internal
   */
  load(): void;

  /**
   * Check if this ad slot was loaded and is ready to be played
   *
   * @internal
   *
   * TODO: Visibility was changed from `public` to `internal`!
   */
  isLoaded(): boolean;

  /**
   * Start playback for this ad slot
   *
   * @internal
   */
  start(): void;

  /**
   * Stop playback for this ad slot
   *
   * @internal
   */
  stop(): void;

  /**
   * Mark playback as completed
   *
   * @internal
   */
  complete(): void;

  /**
   * Mark playback as failed
   *
   * @internal
   * @param reason Specify the reason for failure
   */
  fail(reason: unknown): void;

  /**
   * Get the reason for ad slot failure
   *
   * @internal
   */
  getError(): unknown;

  /**
   * Reset the ad slot: mark it as uninitialized, not failed, not completed, not
   * active.
   *
   * @internal
   */
  reset(): void;

  /**
   * Get a string representation of the ad slot, useful for logging.
   *
   * @internal
   */
  toString(withState?: boolean): string;
};
